import { setLoading, setUser, setError, clearAuthState } from './authSlice';
// import { toast } from 'react-toastify';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import axios from 'axios';
import { API_ROUTES } from '../Constants/apiRoutes';
import { auth } from '../../firebase';
import { cleanFirebaseError } from '../UtilsRedux/cleanErrorMessage.js';
import { showFeedback } from '../UI/uiFeedbackSlice';

// Normalize helper
const normalizeUser = (data) =>
  data?.userFlat || {
    ...data.user,
    entitlements: data.entitlements,
    paymentDetails: data.paymentDetails,
    subscription: data.subscription,
  };

// ------------------ Sign Up (email/password) ------------------
const signUp = (fullName, email, password, navigate) => async (dispatch) => {
  try {
    dispatch(setLoading(true));

    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const token = await cred.user.getIdToken();
    localStorage.setItem('token', token);

    // Backward-compat path: POST /users/me (server mirrors to GET logic)
    await axios.post(API_ROUTES.SIGN_UP, { fullName, email }, {
      headers: { Authorization: `Bearer ${token}` }
    });

    // Fetch full profile (so state has uid/plan/etc.)
    const me = await axios.get(API_ROUTES.SIGN_IN, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const payload = normalizeUser(me.data);

    dispatch(setUser(payload));
    localStorage.setItem('user', JSON.stringify(payload));

    // ✅ success popup
    dispatch(
      showFeedback({
        type: 'success',
        title: 'Account created',
        message: 'Your Site360 account has been created successfully.',
      })
    );
navigate('/dashboard');

    // navigate('/onboarding'); // keep your onboarding step
  } catch (err) {
    const message = cleanFirebaseError(err);
    dispatch(setError(message));
    // ✅ error popup
    dispatch(
      showFeedback({
        type: 'error',
        title: 'Sign up failed',
        message,
      })
    );
  } finally {
    dispatch(setLoading(false));
  }
};

// ------------------ Sign In (email/password) ------------------
const signIn = (email, password, navigate, keepSignedIn = false) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    await setPersistence(auth, keepSignedIn ? browserLocalPersistence : browserSessionPersistence);

    const cred = await signInWithEmailAndPassword(auth, email, password);
    const token = await cred.user.getIdToken();
    localStorage.setItem('token', token);

    const res = await axios.get(API_ROUTES.SIGN_IN, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const payload = normalizeUser(res.data);
    dispatch(setUser(payload));
    localStorage.setItem('user', JSON.stringify(payload));


    // ✅ success popup
    dispatch(
      showFeedback({
        type: 'success',
        title: 'Signed in',
        message: 'Welcome back to Site360.',
      })
    );

    navigate('/dashboard');
  } catch (err) {
    const message = cleanFirebaseError(err);
    dispatch(setError(message));
    // ✅ error popup
    dispatch(
      showFeedback({
        type: 'error',
        title: 'Sign in failed',
        message,
      })
    );
  } finally {
    dispatch(setLoading(false));
  }
};

// ------------------ Google Sign Up ------------------
const signUpWithGoogle = (navigate) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const token = await result.user.getIdToken();
    localStorage.setItem('token', token);

    // Just hitting /me (server will auto-create user if not present)
    const res = await axios.get(API_ROUTES.SIGN_IN, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const payload = normalizeUser(res.data);
    dispatch(setUser(payload));
    localStorage.setItem('user', JSON.stringify(payload));

    dispatch(
      showFeedback({
        type: 'success',
        title: 'Account created',
        message: 'You have signed up with Google successfully.',
      })
    );

    navigate('/dashboard');
  } catch (err) {
    const message = cleanFirebaseError(err);
    dispatch(setError(message));
    dispatch(
      showFeedback({
        type: 'error',
        title: 'Google sign up failed',
        message,
      })
    );
  } finally {
    dispatch(setLoading(false));
  }
};

// ------------------ Google Sign In ------------------
const signInWithGoogle = (navigate, keepSignedIn = false) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    await setPersistence(auth, keepSignedIn ? browserLocalPersistence : browserSessionPersistence);

    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const token = await result.user.getIdToken();
    localStorage.setItem('token', token);

    const res = await axios.get(API_ROUTES.SIGN_IN, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const payload = normalizeUser(res.data);
    dispatch(setUser(payload));
    localStorage.setItem('user', JSON.stringify(payload));

    dispatch(
      showFeedback({
        type: 'success',
        title: 'Signed in',
        message: 'You are now signed in with Google.',
      })
    );

    navigate('/dashboard');
  } catch (err) {
    const message = cleanFirebaseError(err);
    dispatch(setError(message));
    dispatch(
      showFeedback({
        type: 'error',
        title: 'Google sign in failed',
        message,
      })
    );
  } finally {
    dispatch(setLoading(false));
  }
};

export const logoutUser = () => (dispatch) => {
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  dispatch(clearAuthState());

  // optional, but nice:
  dispatch(
    showFeedback({
      type: 'success',
      title: 'Logged out',
      message: 'You have been signed out of Site360.',
    })
  );
};

// ------------------ Update Onboarding ------------------
const updateOnboarding = (formData, token, navigate) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const res = await axios.put(API_ROUTES.ONBOARDING_UPDATE, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // If your backend returns only {success,user}, fetch /me to ensure entitlements
    const me = await axios.get(API_ROUTES.SIGN_IN, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const payload = normalizeUser(me.data);

    dispatch(setUser(payload));
    localStorage.setItem('user', JSON.stringify(payload));

    dispatch(
      showFeedback({
        type: 'success',
        title: 'Profile updated',
        message: 'Your onboarding details have been saved.',
      })
    );


    navigate('/dashboard');
  } catch (err) {
    const message = cleanFirebaseError(err);
    dispatch(setError(message));

    dispatch(
      showFeedback({
        type: 'error',
        title: 'Update failed',
        message,
      })
    );
  } finally {
    dispatch(setLoading(false));
  }
};

export { signIn, signUp, signInWithGoogle, signUpWithGoogle, updateOnboarding };
